import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import {
  type CronFormValue,
  FormCron,
} from '@ipfabric/ui-components/FormCron/FormCron';
import { FormSwitch } from '@ipfabric/ui-components/FormSwitch/FormSwitch';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import { parseExpression } from 'cron-parser';
import { useEffect } from 'react';
import { type FC, memo } from 'react';
import { FormProvider, useWatch } from 'react-hook-form';
import z from 'zod';
import type { CronFormValue as CronFormValueType } from '../types';

export type MaintenanceFormFields = {
  enabled: boolean;
  cron: CronFormValue;
};

interface MaintenanceProps {
  initialValues?: {
    enabled: string | boolean;
    cron: CronFormValueType;
  };
  onStartMaintenance: () => void;
  onSubmit: (values: MaintenanceFormFields) => void;
  onSubmitFail?: () => void;
  loading?: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
}

/**
 * Normalizes enabled value from string or boolean to boolean
 */
function normalizeEnabledValue(value: string | boolean | undefined): boolean {
  return value === true || value === 'true';
}

/**
 * Converts initial cron values to form format
 */
function normalizeCronValue(
  cron: CronFormValueType | undefined,
): CronFormValue | undefined {
  if (!cron) {
    return undefined;
  }
  return {
    expression: cron.expression,
    timezone: cron.timezone,
  };
}

/**
 * Creates default form values from initial values
 */
function getDefaultFormValues(
  initialValues?: MaintenanceProps['initialValues'],
): Partial<MaintenanceFormFields> {
  return {
    enabled: normalizeEnabledValue(initialValues?.enabled),
    cron: normalizeCronValue(initialValues?.cron),
  };
}

/**
 * Validates cron expression syntax
 */
function isValidCronExpression(expression: string): boolean {
  try {
    parseExpression(expression);
    return true;
  } catch {
    return false;
  }
}

const validationSchema = z
  .object({
    enabled: z.boolean(),
    cron: z
      .object({
        expression: z.string(),
        timezone: z.string(),
      })
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.enabled) {
      return;
    }

    if (!data.cron || typeof data.cron !== 'object') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Cron schedule is required',
        path: ['cron'],
      });
      return;
    }

    const expression = data.cron.expression;

    if (!expression || expression.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Cron schedule is required',
        path: ['cron', 'expression'],
      });
      return;
    }

    if (!isValidCronExpression(expression)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid cron expression',
        path: ['cron', 'expression'],
      });
    }
  });

const BareMaintenance: FC<MaintenanceProps> = ({
  initialValues,
  onStartMaintenance,
  onSubmit,
  onSubmitFail,
  onDirtyChange,
}) => {
  const formMethods = useCustomForm<MaintenanceFormFields>({
    defaultValues: getDefaultFormValues(initialValues),
    resolver: zodResolver(validationSchema),
  });

  const { control, reset, handleSubmit, formState } = formMethods;

  const enabled = useWatch({
    control,
    name: 'enabled',
  });

  useEffect(() => {
    reset(getDefaultFormValues(initialValues));
  }, [initialValues, reset]);

  useEffect(() => {
    if (!enabled) {
      reset(undefined, {
        keepValues: true,
        keepDirty: true,
        keepIsSubmitted: false,
        keepErrors: false,
      });
    }
  }, [enabled, reset]);

  useEffect(() => {
    onDirtyChange?.(formState.isDirty);
  }, [formState.isDirty, onDirtyChange]);

  const isSubmitDisabled = !formState.isValid;

  return (
    <Card className='p flex flex-col gap'>
      <div className='text-level-5 font-semibold'>
        Schedule system maintenance
      </div>
      <FormProvider {...formMethods}>
        <form
          onSubmit={handleSubmit(onSubmit, onSubmitFail)}
          className='flex flex-col gap'
          autoComplete='off'
        >
          <FormSwitch
            control={control}
            name='enabled'
            label='Enable maintenance job'
          />
          {enabled && <FormCron control={control} name='cron' />}
          <div className='flex gap-xs max-w-96'>
            <Button
              className='flex-grow'
              variant='primary'
              type='submit'
              disabled={isSubmitDisabled}
              icon='fa-save'
            >
              Save
            </Button>
            <Button
              className='flex-grow'
              variant='outline'
              type='button'
              onClick={onStartMaintenance}
            >
              Start maintenance
            </Button>
          </div>
        </form>
      </FormProvider>
    </Card>
  );
};

export const Maintenance = memo(BareMaintenance);
